import { useRouter } from 'next/router';
import { ParsedUrlQuery, ParsedUrlQueryInput, stringify } from 'querystring';
import { FiltersQuery } from 'types';
import { useEffect, useState } from 'react';
import { getHashQuery, getValidFiltersFromQuery } from 'utils/filters/filters';

const removeNullValuesFromQueryObject = (query: FiltersQuery): FiltersQuery => {
  const queryEntries = Object.entries(query);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const filteredEntries = queryEntries.filter(([_, v]) => Boolean(v));
  return Object.fromEntries(filteredEntries);
};

const getNewQuery = (
  query: ParsedUrlQuery,
  asPath: string,
  filter: FiltersQuery,
  defaultRadius: string,
) => {
  const hash = getHashQuery(asPath);
  // If postcode, bounds or central coordinates (aka [location]) not updated => only update filters & [location] unchanged
  if (!filter.location && !filter.seLat) {
    const overwrittenQuery: FiltersQuery = {
      ...query,
      ...hash,
      ...filter,
      ...(filter.distance && {
        distance: filter.distance,
      }),
    };
    return removeNullValuesFromQueryObject(overwrittenQuery);
  }
  // If filter is postcode only remove coordinates
  else if ((filter.location && !filter.seLat) || (filter.distance && !filter.seLat)) {
    const overwrittenQuery: FiltersQuery = {
      ...query,
      ...hash,
      ...filter,
      distance: filter.distance || defaultRadius,
    };

    const { seLng, seLat, nwLng, nwLat, ...queryWithoutCoordinates } = overwrittenQuery;

    // remove null values from query object
    return removeNullValuesFromQueryObject(queryWithoutCoordinates);
  }
  // If filter are map bounds coordinates, remove distance, central coordinates and replace around with 'map'
  const aroundMapLocation = { location: `around-${filter.location || 'map-area'}` };

  const overwrittenQuery = {
    ...query,
    ...hash,
    ...filter,
    ...(filter.seLng && {
      ...aroundMapLocation,
      distance: '',
    }),
  };
  const { latitude, longitude, ...queryWithoutCentralCoordinates } = overwrittenQuery;
  return removeNullValuesFromQueryObject(queryWithoutCentralCoordinates);
};

export const useActivityFilters = (defaultRadius: string) => {
  const router = useRouter();

  const [queryState, setQueryState] = useState<FiltersQuery>(
    getValidFiltersFromQuery(router.query, router.asPath, defaultRadius),
  );

  useEffect(() => {
    const routerFilters = { ...getHashQuery(router.asPath), ...router.query };
    // if filters in url & queryState are already in sync, return.
    if (JSON.stringify(routerFilters) === JSON.stringify(queryState)) {
      return;
    }

    const { location, category, page, ...rest } = queryState;

    const asyncPush = async () => {
      try {
        await router.push({
          query: {
            location,
            category,
            ...(page && page !== '1' && { page }),
          },
          hash: stringify(rest as ParsedUrlQueryInput),
        });
      } catch (error) {
        // silently capture the error
      }
    };

    if (location && category) {
      asyncPush();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryState]);

  const onFilterChange = (filter: FiltersQuery) => {
    const updatedQuery = getNewQuery(router.query, router.asPath, filter, defaultRadius);
    setQueryState(updatedQuery);
  };

  const resetAllFilters = () => {
    const { location, latitude, longitude } = queryState;
    setQueryState(
      removeNullValuesFromQueryObject({
        distance: defaultRadius,
        category: 'all-activities',
        location,
        latitude,
        longitude,
      }),
    );
  };

  const resetCategoryFilter = () => {
    setQueryState((prev) =>
      removeNullValuesFromQueryObject({
        ...prev,
        category: 'all-activities',
      }),
    );
  };

  const resetTimeFilter = () => {
    const { time, ...rest } = queryState;
    setQueryState({
      ...removeNullValuesFromQueryObject(rest),
    });
  };

  return {
    selectedFilters: queryState,
    onFilterChange,
    resetAllFilters,
    resetCategoryFilter,
    resetTimeFilter,
  };
};
